import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../contexts/AuthContext';
import { toast } from 'sonner';
import Header from '../components/Header';
import ImageUploader from '../components/ImageUploader';
import SlugEditor from '../components/SlugEditor';
import { 
  analyzeContentForSchemas, 
  generateCompleteSchema 
} from '../lib/blog';
import { 
  FileText, 
  FolderTree, 
  Plus, 
  Search, 
  Settings, 
  Eye, 
  Save, 
  ChevronRight, 
  Check, 
  AlertCircle, 
  Link as LinkIcon,
  Trash2,
  ArrowLeft,
  Download
} from 'lucide-react';
import { Button } from '../components/ui/button';

const SCHEMA_TEMPLATES = [
  { id: 'article', name: 'Article de base', description: 'Article standard avec breadcrumb (toujours inclus)', types: ['Article', 'BreadcrumbList'] },
  { id: 'howto', name: 'Guide pratique (How-To)', description: 'Guide avec étapes numérotées', types: ['HowTo'] },
  { id: 'faq', name: 'Questions/Réponses (FAQ)', description: 'Article avec section FAQ', types: ['FAQPage'] },
  { id: 'definition', name: 'Définition de terme', description: 'Article définissant un concept', types: ['DefinedTerm'] },
  { id: 'software', name: 'Application logicielle', description: "Présentation d'un logiciel/outil", types: ['SoftwareApplication'] },
];

const BlogManagerPage = () => {
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState('dashboard');
  const [editorStep, setEditorStep] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedSchemas, setSelectedSchemas] = useState(['article']); // article always included by default
  const [schemaRecommendations, setSchemaRecommendations] = useState([]);
  const [schemaConfidence, setSchemaConfidence] = useState({ article: 1.0 });
  const [extractedSchemaData, setExtractedSchemaData] = useState({});
  
  const [posts, setPosts] = useState([]);
  const [currentPost, setCurrentPost] = useState({
    type: 'pillar',
    status: 'draft',
    tags: [],
    schemaOrg: {}
  });

  // Update recommendations when content changes using the schema-detector library
  useEffect(() => {
    if (currentPost.content && currentPost.title) {
      const analysis = analyzeContentForSchemas(
        currentPost.content, 
        currentPost.title, 
        currentPost.excerpt || ''
      );
      setSchemaRecommendations(analysis.recommendedSchemas);
      setSchemaConfidence(analysis.confidence);
      setExtractedSchemaData(analysis.extractedData);
    }
  }, [currentPost.content, currentPost.title, currentPost.excerpt]);

  // Fetch posts from API
  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/blog/posts');
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const pillarPosts = posts.filter(p => p.type === 'pillar');

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/--+/g, '-')
      .trim();
  };

  const generateUrl = (post) => {
    const baseUrl = 'https://aidatacleaner.xyz/blog';
    
    if (post.type === 'pillar') {
      return `${baseUrl}/${post.slug || 'your-slug'}`;
    }
    
    const pillar = posts.find(p => p.id === post.pillarPageId);
    if (!pillar) return `${baseUrl}/[pillar-page]/${post.slug || 'your-slug'}`;
    
    return `${baseUrl}/${pillar.slug}/${post.slug || 'your-slug'}`;
  };

  const calculateSEOScore = (post) => {
    let score = 0;
    let checks = [];
    
    if (post.title && post.title.length >= 30 && post.title.length <= 60) {
      score += 20;
      checks.push({ label: 'Titre optimisé (30-60 caractères)', status: 'success' });
    } else {
      checks.push({ label: 'Titre optimisé (30-60 caractères)', status: 'warning' });
    }
    
    if (post.metaDescription && post.metaDescription.length >= 150 && post.metaDescription.length <= 160) {
      score += 20;
      checks.push({ label: 'Meta description optimisée (150-160 car.)', status: 'success' });
    } else {
      checks.push({ label: 'Meta description optimisée (150-160 car.)', status: 'warning' });
    }
    
    if (post.featuredImage) {
      score += 15;
      checks.push({ label: 'Image featured présente', status: 'success' });
    } else {
      checks.push({ label: 'Image featured présente', status: 'warning' });
    }
    
    if (post.slug && post.slug.length <= 60) {
      score += 15;
      checks.push({ label: 'Slug URL optimisé', status: 'success' });
    } else {
      checks.push({ label: 'Slug URL optimisé', status: 'warning' });
    }
    
    if (post.content && post.content.length >= 1000) {
      score += 15;
      checks.push({ label: 'Contenu suffisant (>1000 caractères)', status: 'success' });
    } else {
      checks.push({ label: 'Contenu suffisant (>1000 caractères)', status: 'warning' });
    }
    
    if (post.tags && post.tags.length >= 3) {
      score += 15;
      checks.push({ label: 'Tags appropriés (3+)', status: 'success' });
    } else {
      checks.push({ label: 'Tags appropriés (3+)', status: 'warning' });
    }
    
    return { score, checks };
  };

  const savePost = async (publish = false) => {
    try {
      setSaving(true);
      const postData = {
        ...currentPost,
        status: publish ? 'published' : 'draft'
      };
      
      let response;
      if (currentPost.id) {
        response = await api.put(`/api/blog/posts/${currentPost.id}`, postData);
        toast.success(publish ? 'Article publié!' : 'Brouillon sauvegardé!');
      } else {
        response = await api.post('/api/blog/posts', postData);
        toast.success(publish ? 'Article créé et publié!' : 'Brouillon créé!');
      }
      
      await fetchPosts();
      setCurrentView('dashboard');
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article?')) return;
    
    try {
      await api.delete(`/api/blog/posts/${postId}`);
      toast.success('Article supprimé');
      await fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const renderDashboard = () => {
    const filteredPosts = posts.filter(post => 
      post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.slug?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Blog Manager</h1>
            <p className="text-gray-600 mt-1">Gérez vos pages piliers et articles clusters</p>
          </div>
          <Button
            onClick={() => {
              setCurrentPost({ type: 'pillar', status: 'draft', tags: [], schema: {} });
              setEditorStep(1);
              setCurrentView('editor');
            }}
            className="bg-blue-600 text-white hover:bg-blue-700"
          >
            <Plus className="w-5 h-5 mr-2" />
            Nouvel Article
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-600">Pages Piliers</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">
              {posts.filter(p => p.type === 'pillar').length}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-600">Articles Clusters</div>
            <div className="text-2xl font-bold text-gray-900 mt-1">
              {posts.filter(p => p.type === 'cluster').length}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-600">Publiés</div>
            <div className="text-2xl font-bold text-green-600 mt-1">
              {posts.filter(p => p.status === 'published').length}
            </div>
          </div>
          <div className="bg-white p-4 rounded-lg border border-gray-200">
            <div className="text-sm text-gray-600">Brouillons</div>
            <div className="text-2xl font-bold text-yellow-600 mt-1">
              {posts.filter(p => p.status === 'draft').length}
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Rechercher un article..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {/* Content Tree */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="p-4 bg-gray-50 border-b border-gray-200">
            <h2 className="font-semibold text-gray-900 flex items-center gap-2">
              <FolderTree className="w-5 h-5" />
              Structure du Blog
            </h2>
          </div>
          
          {loading ? (
            <div className="p-8 text-center text-gray-500">Chargement...</div>
          ) : pillarPosts.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <p>Aucune page pilier créée.</p>
              <Button
                onClick={() => {
                  setCurrentPost({ type: 'pillar', status: 'draft', tags: [], schema: {} });
                  setEditorStep(1);
                  setCurrentView('editor');
                }}
                className="mt-4"
                variant="outline"
              >
                Créer votre première page pilier
              </Button>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {pillarPosts.map(pillar => {
                const clusters = posts.filter(p => p.pillarPageId === pillar.id);
                return (
                  <div key={pillar.id} className="p-4">
                    {/* Pillar Page */}
                    <div className="flex items-start gap-3 mb-2">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-gray-900">{pillar.title}</span>
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                            Pilier
                          </span>
                          {pillar.status === 'published' ? (
                            <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                              Publié
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                              Brouillon
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 mt-0.5">
                          /blog/{pillar.slug}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {clusters.length} article{clusters.length > 1 ? 's' : ''} cluster
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => window.open(`/blog/${pillar.slug}`, '_blank')}
                          className="text-gray-400 hover:text-gray-600"
                          title="Voir"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setCurrentPost(pillar);
                            setEditorStep(1);
                            setCurrentView('editor');
                          }}
                          className="text-blue-600 hover:text-blue-700 text-sm"
                        >
                          Éditer
                        </button>
                        <button
                          onClick={() => deletePost(pillar.id)}
                          className="text-red-400 hover:text-red-600"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Cluster Articles */}
                    {clusters.length > 0 && (
                      <div className="ml-11 mt-2 space-y-2">
                        {clusters.map(cluster => (
                          <div key={cluster.id} className="flex items-start gap-3 p-2 hover:bg-gray-50 rounded-lg">
                            <ChevronRight className="w-4 h-4 text-gray-400 mt-1 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-gray-900">{cluster.title}</span>
                                {cluster.status === 'published' ? (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                                    Publié
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs rounded-full">
                                    Brouillon
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-600 mt-0.5">
                                /blog/{pillar.slug}/{cluster.slug}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => window.open(`/blog/${pillar.slug}/${cluster.slug}`, '_blank')}
                                className="text-gray-400 hover:text-gray-600"
                                title="Voir"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => {
                                  setCurrentPost(cluster);
                                  setEditorStep(1);
                                  setCurrentView('editor');
                                }}
                                className="text-blue-600 hover:text-blue-700 text-sm"
                              >
                                Éditer
                              </button>
                              <button
                                onClick={() => deletePost(cluster.id)}
                                className="text-red-400 hover:text-red-600"
                                title="Supprimer"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Add cluster button */}
                    <div className="ml-11 mt-2">
                      <button
                        onClick={() => {
                          setCurrentPost({ 
                            type: 'cluster', 
                            pillarPageId: pillar.id,
                            status: 'draft', 
                            tags: [], 
                            schemaOrg: {} 
                          });
                          setEditorStep(1);
                          setCurrentView('editor');
                        }}
                        className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Ajouter un article cluster
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderEditor = () => {
    const seoScore = calculateSEOScore(currentPost);
    const generatedUrl = generateUrl(currentPost);

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentView('dashboard')}
            className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour au Dashboard
          </button>
          <div className="flex gap-2">
            <Button 
              variant="outline"
              onClick={() => savePost(false)}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Sauvegarder brouillon'}
            </Button>
            <Button
              onClick={() => savePost(true)}
              disabled={saving}
              className="bg-blue-600 text-white hover:bg-blue-700"
            >
              <Save className="w-4 h-4 mr-2" />
              Publier
            </Button>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            {[
              { num: 1, label: 'Configuration' },
              { num: 2, label: 'Contenu' },
              { num: 3, label: 'SEO & Schema' },
              { num: 4, label: 'Révision' }
            ].map((step, idx) => (
              <React.Fragment key={step.num}>
                <div 
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={() => setEditorStep(step.num)}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold ${
                    editorStep >= step.num ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {editorStep > step.num ? <Check className="w-4 h-4" /> : step.num}
                  </div>
                  <span className={`text-sm ${editorStep >= step.num ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
                    {step.label}
                  </span>
                </div>
                {idx < 3 && (
                  <div className={`flex-1 h-0.5 mx-2 ${editorStep > step.num ? 'bg-blue-600' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Editor */}
          <div className="lg:col-span-2 space-y-6">
            {/* Step 1: Configuration */}
            {editorStep === 1 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
                <h2 className="text-xl font-semibold text-gray-900">Configuration de base</h2>
                
                {/* Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type d'article *
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={() => setCurrentPost({ ...currentPost, type: 'pillar', pillarPageId: undefined })}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        currentPost.type === 'pillar' 
                          ? 'border-blue-600 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold text-gray-900">Page Pilier</div>
                      <div className="text-sm text-gray-600 mt-1">
                        Contenu principal d'un sujet, avec URL courte
                      </div>
                    </button>
                    <button
                      onClick={() => setCurrentPost({ ...currentPost, type: 'cluster' })}
                      className={`p-4 border-2 rounded-lg text-left transition-all ${
                        currentPost.type === 'cluster' 
                          ? 'border-blue-600 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="font-semibold text-gray-900">Article Cluster</div>
                      <div className="text-sm text-gray-600 mt-1">
                        Approfondit un aspect spécifique d'un pilier
                      </div>
                    </button>
                  </div>
                </div>

                {/* Pillar Selection (if cluster) */}
                {currentPost.type === 'cluster' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Page pilier parente *
                    </label>
                    <select
                      value={currentPost.pillarPageId || ''}
                      onChange={(e) => setCurrentPost({ ...currentPost, pillarPageId: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Sélectionner une page pilier...</option>
                      {pillarPosts.map(pillar => (
                        <option key={pillar.id} value={pillar.id}>
                          {pillar.title}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Titre *
                  </label>
                  <input
                    type="text"
                    value={currentPost.title || ''}
                    onChange={(e) => {
                      const title = e.target.value;
                      setCurrentPost({ 
                        ...currentPost, 
                        title,
                        slug: currentPost.slug || generateSlug(title)
                      });
                    }}
                    placeholder="Ex: Complete Guide to CRM Data Deduplication"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="text-sm text-gray-500 mt-1">
                    {currentPost.title?.length || 0} caractères (optimal: 30-60)
                  </div>
                </div>

                {/* Slug Editor */}
                <SlugEditor
                  title={currentPost.title || ''}
                  currentSlug={currentPost.slug || ''}
                  type={currentPost.type || 'pillar'}
                  pillarSlug={
                    currentPost.type === 'cluster' && currentPost.pillarPageId
                      ? posts.find(p => p.id === currentPost.pillarPageId)?.slug || ''
                      : ''
                  }
                  onChange={(slug) => setCurrentPost({ ...currentPost, slug })}
                  existingSlugs={posts
                    .filter(p => p.id !== currentPost.id)
                    .map(p => p.slug)
                  }
                />

                <Button
                  onClick={() => setEditorStep(2)}
                  disabled={!currentPost.title || !currentPost.slug || (currentPost.type === 'cluster' && !currentPost.pillarPageId)}
                  className="w-full bg-blue-600 text-white hover:bg-blue-700"
                >
                  Continuer →
                </Button>
              </div>
            )}

            {/* Step 2: Content */}
            {editorStep === 2 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
                <h2 className="text-xl font-semibold text-gray-900">Contenu de l'article</h2>
                
                {/* Excerpt */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Extrait *
                  </label>
                  <textarea
                    value={currentPost.excerpt || ''}
                    onChange={(e) => setCurrentPost({ ...currentPost, excerpt: e.target.value })}
                    placeholder="Résumé court pour les aperçus..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                  <div className="text-sm text-gray-500 mt-1">
                    {currentPost.excerpt?.length || 0} caractères
                  </div>
                </div>

                {/* Content */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Contenu (Markdown) *
                  </label>
                  <textarea
                    value={currentPost.content || ''}
                    onChange={(e) => setCurrentPost({ ...currentPost, content: e.target.value })}
                    placeholder={`# Introduction

Votre contenu en Markdown...

## Section 1

Texte de la section...`}
                    rows={15}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm resize-none"
                  />
                  <div className="text-sm text-gray-500 mt-1">
                    {currentPost.content?.length || 0} caractères (recommandé: 1000+)
                  </div>
                </div>

                {/* Featured Image with AI Alt Text */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Image featured
                  </label>
                  <ImageUploader
                    currentImage={currentPost.featuredImage || ''}
                    currentAltText={currentPost.metaTitle || ''}
                    onImageUploaded={(result) => {
                      setCurrentPost({
                        ...currentPost,
                        featuredImage: result.url,
                        // Use alt text as base for meta title if empty
                        ...(!currentPost.metaTitle && result.altText ? { metaTitle: result.altText } : {})
                      });
                    }}
                  />
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setEditorStep(1)}
                    className="flex-1"
                  >
                    ← Retour
                  </Button>
                  <Button
                    onClick={() => setEditorStep(3)}
                    disabled={!currentPost.excerpt || !currentPost.content}
                    className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Continuer →
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: SEO & Schema */}
            {editorStep === 3 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
                <h2 className="text-xl font-semibold text-gray-900">SEO & Schema.org</h2>
                
                {/* Meta Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meta Title
                  </label>
                  <input
                    type="text"
                    value={currentPost.metaTitle || ''}
                    onChange={(e) => setCurrentPost({ ...currentPost, metaTitle: e.target.value })}
                    placeholder={currentPost.title || "Titre SEO (par défaut: titre de l'article)"}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="text-sm text-gray-500 mt-1">
                    {(currentPost.metaTitle || currentPost.title || '').length} caractères (optimal: 50-60)
                  </div>
                </div>

                {/* Meta Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meta Description *
                  </label>
                  <textarea
                    value={currentPost.metaDescription || ''}
                    onChange={(e) => setCurrentPost({ ...currentPost, metaDescription: e.target.value })}
                    placeholder="Description qui apparaîtra dans les résultats Google..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                  <div className="text-sm text-gray-500 mt-1">
                    {currentPost.metaDescription?.length || 0} caractères (optimal: 150-160)
                  </div>
                </div>

                {/* Schema Template - Multi-select */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Types de Schema.org
                    </label>
                    {schemaRecommendations.length > 1 && (
                      <button
                        type="button"
                        onClick={() => setSelectedSchemas(schemaRecommendations)}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Appliquer recommandations IA
                      </button>
                    )}
                  </div>
                  
                  {/* AI Recommendations */}
                  {schemaRecommendations.length > 1 && (
                    <div className="mb-3 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <div className="text-purple-600 mt-0.5">✨</div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-purple-900 mb-1">
                            Schemas recommandés par IA
                          </div>
                          <div className="text-xs text-purple-700">
                            Basé sur l&apos;analyse de votre contenu : {schemaRecommendations.filter(s => s !== 'article').map(id => 
                              SCHEMA_TEMPLATES.find(t => t.id === id)?.name
                            ).join(', ')}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-2">
                    {SCHEMA_TEMPLATES.map(template => {
                      const isSelected = selectedSchemas.includes(template.id);
                      const isRecommended = schemaRecommendations.includes(template.id);
                      const isRequired = template.id === 'article';
                      
                      return (
                        <button
                          key={template.id}
                          type="button"
                          onClick={() => {
                            if (isRequired) return; // article always selected
                            
                            if (isSelected) {
                              setSelectedSchemas(selectedSchemas.filter(id => id !== template.id));
                            } else {
                              setSelectedSchemas([...selectedSchemas, template.id]);
                            }
                          }}
                          className={`w-full p-3 border-2 rounded-lg text-left transition-all ${
                            isSelected
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300 bg-white'
                          } ${isRequired ? 'opacity-75 cursor-default' : 'cursor-pointer'}`}
                          data-testid={`schema-${template.id}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                            }`}>
                              {isSelected && <Check size={14} className="text-white" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-gray-900">{template.name}</span>
                                {isRequired && (
                                  <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded">
                                    Requis
                                  </span>
                                )}
                                {isRecommended && !isRequired && (
                                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded flex items-center gap-1">
                                    ✨ Recommandé
                                  </span>
                                )}
                              </div>
                              <div className="text-sm text-gray-600 mt-0.5">{template.description}</div>
                              <div className="flex gap-1 mt-2 flex-wrap">
                                {template.types.map(type => (
                                  <span key={type} className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded">
                                    {type}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                  
                  <div className="text-xs text-gray-500 mt-2">
                    {selectedSchemas.length} schema(s) sélectionné(s) • L&apos;article de base est toujours inclus
                  </div>
                </div>

                {/* Tags - Enhanced Input */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tags (appuyez sur Entrée ou virgule pour ajouter)
                  </label>
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-2 p-2 border border-gray-300 rounded-lg min-h-[42px] bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent">
                      {currentPost.tags?.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm"
                        >
                          {tag}
                          <button
                            type="button"
                            onClick={() => {
                              const newTags = [...(currentPost.tags || [])];
                              newTags.splice(index, 1);
                              setCurrentPost({ ...currentPost, tags: newTags });
                            }}
                            className="hover:text-blue-900 font-bold"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <input
                        type="text"
                        placeholder={currentPost.tags?.length ? "" : "Ajouter un tag..."}
                        className="flex-1 min-w-[120px] outline-none text-sm bg-transparent"
                        onKeyDown={(e) => {
                          const input = e.currentTarget;
                          const value = input.value.trim();
                          
                          if ((e.key === 'Enter' || e.key === ',') && value) {
                            e.preventDefault();
                            const newTags = [...(currentPost.tags || [])];
                            if (!newTags.includes(value)) {
                              newTags.push(value);
                              setCurrentPost({ ...currentPost, tags: newTags });
                            }
                            input.value = '';
                          } else if (e.key === 'Backspace' && !input.value && currentPost.tags?.length) {
                            const newTags = [...currentPost.tags];
                            newTags.pop();
                            setCurrentPost({ ...currentPost, tags: newTags });
                          }
                        }}
                        data-testid="tag-input"
                      />
                    </div>
                    <div className="text-sm text-gray-500">
                      {currentPost.tags?.length || 0} tag(s) • Recommandé: 3-5 tags
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setEditorStep(2)}
                    className="flex-1"
                  >
                    ← Retour
                  </Button>
                  <Button
                    onClick={() => setEditorStep(4)}
                    disabled={!currentPost.metaDescription}
                    className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Continuer →
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Review */}
            {editorStep === 4 && (
              <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-4">
                <h2 className="text-xl font-semibold text-gray-900">Révision finale</h2>
                
                {/* Google Preview */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Aperçu Google</h3>
                  <div className="border border-gray-200 rounded-lg p-4 bg-white">
                    <div className="text-blue-600 text-lg hover:underline cursor-pointer">
                      {currentPost.metaTitle || currentPost.title}
                    </div>
                    <div className="text-green-700 text-sm mt-1">
                      {generatedUrl}
                    </div>
                    <div className="text-gray-600 text-sm mt-2">
                      {currentPost.metaDescription}
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="space-y-3">
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Type</span>
                    <span className="font-medium">
                      {currentPost.type === 'pillar' ? 'Page Pilier' : 'Article Cluster'}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">URL</span>
                    <span className="font-medium text-sm break-all text-right ml-4">
                      {generatedUrl}
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Longueur contenu</span>
                    <span className="font-medium">
                      {currentPost.content?.length || 0} caractères
                    </span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-gray-100">
                    <span className="text-gray-600">Tags</span>
                    <span className="font-medium">
                      {currentPost.tags?.length || 0} tag(s)
                    </span>
                  </div>
                  <div className="py-2 border-b border-gray-100">
                    <div className="text-gray-600 mb-2">Schemas Schema.org</div>
                    <div className="flex flex-wrap gap-1">
                      {selectedSchemas.map(schemaId => {
                        const template = SCHEMA_TEMPLATES.find(t => t.id === schemaId);
                        return (
                          <span key={schemaId} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                            {template?.name}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setEditorStep(3)}
                    className="flex-1"
                  >
                    ← Retour
                  </Button>
                  <Button
                    onClick={() => savePost(true)}
                    disabled={saving}
                    className="flex-1 bg-green-600 text-white hover:bg-green-700"
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Publier l&apos;article
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* SEO Score */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-3">Score SEO</h3>
              <div className="flex items-center justify-center mb-4">
                <div className="relative">
                  <svg className="w-24 h-24 transform -rotate-90">
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="#e5e7eb"
                      strokeWidth="8"
                      fill="none"
                    />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke={seoScore.score >= 80 ? '#10b981' : seoScore.score >= 60 ? '#f59e0b' : '#ef4444'}
                      strokeWidth="8"
                      fill="none"
                      strokeDasharray={`${seoScore.score * 2.51} 251`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-gray-900">{seoScore.score}</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                {seoScore.checks.map((check, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    {check.status === 'success' ? (
                      <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                    )}
                    <span className={check.status === 'success' ? 'text-gray-900' : 'text-gray-600'}>
                      {check.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Tips */}
            <div className="bg-blue-50 rounded-lg border border-blue-200 p-4">
              <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <Settings className="w-4 h-4" />
                Conseils SEO
              </h3>
              <ul className="space-y-2 text-sm text-blue-800">
                <li>• Utilisez des mots-clés dans les 100 premiers mots</li>
                <li>• Ajoutez des sous-titres (H2, H3) tous les 300 mots</li>
                <li>• Incluez des liens internes vers d&apos;autres articles</li>
                <li>• Optimisez l&apos;image avec un alt text descriptif</li>
              </ul>
            </div>

            {/* Schema Confidence */}
            {Object.keys(schemaConfidence).length > 1 && (
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Confiance IA</h3>
                <div className="space-y-2">
                  {Object.entries(schemaConfidence).map(([schemaId, confidence]) => {
                    const template = SCHEMA_TEMPLATES.find(t => t.id === schemaId);
                    if (!template) return null;
                    const confidencePercent = Math.round(confidence * 100);
                    return (
                      <div key={schemaId} className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 w-24 truncate">{template.name}</span>
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${confidencePercent >= 70 ? 'bg-green-500' : confidencePercent >= 40 ? 'bg-yellow-500' : 'bg-gray-400'}`}
                            style={{ width: `${confidencePercent}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 w-10">{confidencePercent}%</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Export Schema */}
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-900 mb-2">Export Schema.org</h3>
              <p className="text-sm text-gray-600 mb-3">
                Schéma généré automatiquement pour cet article
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  // Find pillar info for cluster posts
                  let pillarSlug = '';
                  let pillarTitle = '';
                  if (currentPost.type === 'cluster' && currentPost.pillarPageId) {
                    const pillar = posts.find(p => p.id === currentPost.pillarPageId);
                    if (pillar) {
                      pillarSlug = pillar.slug;
                      pillarTitle = pillar.title;
                    }
                  }
                  
                  // Generate complete schema
                  const schema = generateCompleteSchema(
                    currentPost,
                    selectedSchemas,
                    {
                      steps: extractedSchemaData.steps,
                      faqs: extractedSchemaData.faqs,
                      termName: extractedSchemaData.definitions?.[0]?.term,
                      termDefinition: extractedSchemaData.definitions?.[0]?.definition,
                      softwareName: 'CRM Data Cleaner',
                      softwareFeatures: extractedSchemaData.softwareFeatures,
                    },
                    pillarSlug,
                    pillarTitle
                  );
                  
                  // Download as JSON file
                  const blob = new Blob([JSON.stringify(schema, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `schema-${currentPost.slug || 'article'}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                  toast.success('Schema.json téléchargé !');
                }}
              >
                <Download className="w-4 h-4 mr-2" />
                Télécharger schema.json
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="pt-20 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {currentView === 'dashboard' ? renderDashboard() : renderEditor()}
        </div>
      </main>
    </div>
  );
};

export default BlogManagerPage;
